/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-labels */
import React, { useEffect, useState } from 'react';
import WithAuth from '../hoc/WithAuth';
import TableAction from '../components/TableAction';
import TableFilter from '../components/TableFilter';
import { useGetStatusCount } from '../hooks/useGetStatusCount';
import { getStatusColor } from '../util/statusColor';
import { Button, Input, message, Table, TableProps } from 'antd';
import { useGetPayments, useMakePayment } from '../api/redemption';
import { AuthContextValue, useAuth } from '../providers/AuthProvider';

type TableRowSelection<T extends object = object> =
  TableProps<T>['rowSelection'];

function RedemptionsPage() {
  const { user } = useAuth() as AuthContextValue;
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [statuses, setStatuses] = useState<any>({
    pending: 0,
    paid: 0,
  });

  const { data: payments, refetch: fetchPayments, isLoading } = useGetPayments();
  const { mutateAsync: makePayment, isPending: makingPayment } =
    useMakePayment();

  const [tableData, setTableData] = useState<
    {
      redemptionId: number;
      agroDealerName: string;
      agroDealerPhoneNo: string;
      redemedBy: string;
      redemerPhoneNo: string;
      redemptionDate: string;
      farmerName: string;
      inputName: string;
      redemptionCode: string;
      totalPrice: number;
      txnNo: string;
      paymentStatus: string;
      bankName: string;
      bankId: string;
      bankAccount: string;
    }[]
  >([]);

  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const [transactionNumbers, setTransactionNumbers] = useState<
    {
      redemptionId: number;
      txnNo: string;
      redemptionCode: string;
      saveStatus: boolean;
    }[]
  >([]);

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    if (payments && payments.data && payments.data.data) {
      const modifiedData = payments.data.data.map((item: any) => ({
        ...item,
      }));

      // Compute status counts
      const paid = modifiedData.filter(
        (item: any) => item.paymentStatus === 'PAYED'
      ).length;
      const pending = modifiedData.length - paid;

      setStatuses({ paid, pending });

      let filteredData = modifiedData;

      if (searchText) {
        filteredData = modifiedData.filter(
          (item: any) =>
            item.agroDealerName
              ?.toLowerCase()
              .includes(searchText.toLowerCase()) ||
            item.farmerName?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.redemedBy?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.txnNo?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.paymentStatus?.toLowerCase().includes(searchText.toLowerCase())
        );
      }

      if (selectedFilterIndex !== 0) {
        const selectedStatus = filters[selectedFilterIndex].label;
        filteredData = filteredData.filter(
          (item: any) => item.paymentStatus === selectedStatus
        );
      }

      setTableData(filteredData);
    }
  }, [payments, searchText, selectedFilterIndex]);

  const filters = useGetStatusCount(statuses);

  const adVoucherColumns = [
    {
      dataIndex: 'farmerName',
      title: 'Farmer',
    },
    {
      dataIndex: 'inputName',
      title: 'Input',
      render: (input: string, record: any) => (
        <>{`${input}-${record.redemptionCode}`}</>
      ),
    },
    {
      dataIndex: 'totalPrice',
      title: 'Total Price',
    },
    {
      dataIndex: 'txnNo',
      title: 'Transaction.',
      render: (transactionNo: string, record: any) => {
        if (record.paymentStatus === 'PAYED') {
          return <>{transactionNo}</>;
        } else {
          return <>N/A</>;
        }
      },
    },
    {
      dataIndex: 'redemedBy',
      title: 'Redeemed By',
      render: (fullName: string, record: any) => (
        <div className="flex flex-col items-start">
          <div>{fullName}</div>
          <div className="text-gray-400">{record.redemerPhoneNo}</div>
        </div>
      ),
    },

    {
      dataIndex: 'redemptionDate',
      title: 'Redemption Date',
    },
    {
      dataIndex: 'paymentStatus',
      title: 'Payment Status',
      render: (status: String) => {
        const _status = status === 'PAYED' ? 'PAID' : 'PENDING';
        return (
          <div
            className={`flex justify-center items-center w-4/5 py-[6px] rounded-xl`}
            style={{
              color: getStatusColor(_status),
              backgroundColor: `${getStatusColor(_status)}44`,
            }}
          >
            {status !== 'PAYED' ? 'Pending' : 'Paid'}
          </div>
        );
      },
    },
  ];

  const bankVoucherColumns = [
    {
      dataIndex: 'agroDealerName',
      title: 'Union',
    },
    {
      dataIndex: 'farmerName',
      title: 'Farmer',
    },
    {
      dataIndex: 'inputName',
      title: 'Input',
      render: (input: string, record: any) => (
        <>{`${input}-${record.redemptionCode}`}</>
      ),
    },
    {
      dataIndex: 'totalPrice',
      title: 'Price',
    },
    {
      dataIndex: 'txnNo',
      title: 'Transaction',
      render: (transactionNo: string, record: any) => {
        if (record.paymentStatus === 'PAYED') {
          return <>{transactionNo}</>;
        } else {
          if (selectedRowKeys.includes(record.redemptionId)) {
            return (
              <Input
                key={`input-${record.redemptionId}`}
                style={{ height: 30 }}
                onChange={(e: any) => {
                  const transactionNumber = e.target.value;
                  const _transactionNumbers = transactionNumbers;
                  const _transactionNo = _transactionNumbers.find(
                    (item: { redemptionId: number; txnNo: string }) =>
                      item.redemptionId == record.redemptionId
                  );
                  if (!_transactionNo) {
                    _transactionNumbers.push({
                      redemptionId: record.redemptionId,
                      redemptionCode: record.redemptionCode,
                      txnNo: transactionNumber,
                      saveStatus: false,
                    });
                    setTransactionNumbers(_transactionNumbers);
                  } else {
                    const _transactionNos = _transactionNumbers.filter(
                      (item: { redemptionId: number; txnNo: string }) =>
                        item.redemptionId != record.redemptionId
                    );
                    _transactionNos.push({
                      redemptionId: record.redemptionId,
                      redemptionCode: record.redemptionCode,
                      txnNo: transactionNumber,
                      saveStatus: false,
                    });
                    setTransactionNumbers(_transactionNos);
                  }
                }}
              />
            );
          } else {
            return <>N/A</>;
          }
        }
      },
    },
    {
      dataIndex: 'bankAccount',
      title: 'Account',
      render: (bankAccount: any) => {
        return bankAccount ? bankAccount: 'N/A'; 
      },
    },    
    {
      dataIndex: 'redemedBy',
      title: 'Redeemed By',
      render: (fullName: string, record: any) => (
        <div className="flex flex-col items-start">
          <div>{fullName}</div>
          <div className="text-gray-400">{record.redemerPhoneNo}</div>
        </div>
      ),
    },

    {
      dataIndex: 'redemptionDate',
      title: 'Redemption Date',
    },
    {
      dataIndex: 'paymentStatus',
      title: 'Payment Status',
      render: (status: String) => {
        const _status = status === 'PAYED' ? 'PAID' : 'PENDING';
        return (
          <div
            className={`flex justify-center items-center w-4/5 py-[6px] rounded-xl`}
            style={{
              color: getStatusColor(_status),
              backgroundColor: `${getStatusColor(_status)}44`,
            }}
          >
            {status !== 'PAYED' ? 'Pending' : 'Paid'}
          </div>
        );
      },
    },
    {
      dataIndex: 'redemptionId',
      title: 'Action',
      render: (redemptionId: number, record: any) => (
        <Button
          // loading={makingPayment}
          disabled={record.paymentStatus === 'PAYED'}
          onClick={async () => {
            const _transactionNo = transactionNumbers.find(
              (item: {
                redemptionId: number;
                txnNo: string;
                redemptionCode: string;
                saveStatus: boolean;
              }) => item.redemptionId === redemptionId
            );
            if (_transactionNo && _transactionNo.txnNo) {
              try {
                const result = await makePayment([_transactionNo]);
                if (result.status === 200) {
                  message.success('Update payment status successfully.');
                }
              } catch (error) {
                message.error('Operation failed.');
              }
            }
          }}
        >
          Update
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const _data = filterPaymentByStatus(selectedFilterIndex);

    setTableData(_data);
  }, [selectedFilterIndex]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    if (newSelectedRowKeys) {
      setSelectedRowKeys(newSelectedRowKeys);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => ({
      disabled: record.paymentStatus === 'PAYED',
    }),
  };

  const updateManyHandler = async () => {
    try {
      const result = await makePayment(transactionNumbers);
      if (result.status === 200) {
        message.success('Update payment status successfully.');
      }
    } catch (error) {
      message.error('Operation failed.');
    }
  };

  const filterPaymentByStatus = (data: any) => {
    if (payments && payments.data && payments.data.data) {
      if (selectedFilterIndex === 0) {
        return payments.data.data;
      } else if (selectedFilterIndex === 1) {
        return payments.data.data.filter(
          (item: any) => item.paymentStatus === 'PAYED'
        );
      } else if (selectedFilterIndex === 2) {
        return payments.data.data.filter(
          (item: any) => item.paymentStatus !== 'PAYED'
        );
      }
    }
  };

  return (
    <div className="bg-transparent md:bg-white md:rounded-2 md:px-[50px] md:py-5 md:mt-[25px] md:pb-[22px] rounded-xl">
      <div className="hidden md:block">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 80,
            backgroundColor: '#EEF3F8',
            borderRadius: 8.4,
            marginBottom: 12,
          }}
        >
          <TableAction setSearchText={setSearchText} />
        </div>
        <div className="w-full flex justify-between items-center">
          <TableFilter
            defaultIndex={defaultIndex}
            filters={filters}
            setSelectedFilterIndex={setSelectedFilterIndex}
          />
          {selectedRowKeys.length !== 0 && (
            <Button onClick={updateManyHandler}>Update Selected</Button>
          )}
        </div>
        <Table
          dataSource={tableData}
          loading={isLoading}
          columns={
            user?.roleName === 'ROLE_MFI_ADMIN' ||
            user?.roleName === 'ROLE_MFI_OPERATION'
              ? bankVoucherColumns
              : adVoucherColumns
          }
          rowSelection={
            user &&
            (user.roleName === 'ROLE_MFI_OPERATION' ||
              user.roleName === 'ROLE_MFI_ADMIN')
              ? rowSelection
              : undefined
          }
          rowKey={(record: any) => record.redemptionId}
        />
      </div>
    </div>
  );
}

export default WithAuth(RedemptionsPage);
